import { ReactNode, createContext, useContext, useEffect, useState } from 'react'

import * as api from 'api/api'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'jopath.theme'
const isThemePreference = (value: string): value is ThemePreference => value === 'light' || value === 'dark' || value === 'system'

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches

const applyTheme = (preference: ThemePreference) => {
  const dark = preference === 'dark' || (preference === 'system' && prefersDark())
  document.documentElement.classList.toggle('dark', dark)
}

const hasStoredChoice = (): boolean => {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY))
  } catch {
    return false
  }
}

const readStored = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
  } catch {
    return 'system'
  }
}

const ThemeContext = createContext<{ theme: ThemePreference; resolvedDark: boolean; setTheme: (value: ThemePreference) => void }>({
  resolvedDark: false,
  setTheme: () => undefined,
  theme: 'system',
})

/** Applies the user's theme preference as a `.dark` class on <html>; index.html runs the same check inline to avoid a flash. */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemePreference>(readStored)
  const [resolvedDark, setResolvedDark] = useState(() => theme === 'dark' || (theme === 'system' && prefersDark()))

  useEffect(() => {
    // Settings → Appearance "default for everyone": only applies when the user has never picked their own
    // theme here — an explicit choice always wins. Not persisted, so it keeps following the live org default.
    if (hasStoredChoice()) return
    api
      .fetchAppSettings()
      .then((settings) => {
        if (!hasStoredChoice() && isThemePreference(settings.defaultTheme)) setThemeState(settings.defaultTheme)
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    const sync = () => {
      applyTheme(theme)
      setResolvedDark(theme === 'dark' || (theme === 'system' && prefersDark()))
    }
    sync()
    if (theme !== 'system') return undefined
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [theme])

  const setTheme = (value: ThemePreference) => {
    setThemeState(value)
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      // per-browser preference only; safe to ignore storage failures (private mode, quota, etc.)
    }
  }

  return <ThemeContext.Provider value={{ resolvedDark, setTheme, theme }}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)
